import _, { noop } from 'lodash'
import React, { useMemo, useCallback, useRef, useContext } from 'react'
import { Formik, Form } from 'formik'
import { useNewTranslation } from 'hooks'
import { StepState, TaskState, EngagementId } from 'state/tasks'
import { selectRegistrationDate } from 'state/registrations'
import { showSnackBar } from 'services/toast'
import OnFormikValidationError from 'components/OnFormikValidationError'
import { convertToBackendStr, parseBackendDate, addDay } from 'services/dates'
import * as yup from 'yup'
import {
    TASK_TITLE_SCHEMA,
    EXTRA_PAYOUT_SCHEMA,
    STEP_DATA_SCHEMAS,
    PAYOUTS_SCHEMA,
    CUSTOM_STEP_BLOCK_SCHEMA,
    STEP_TIME_WINDOW_SCHEMA,
    SCHEMA_TIME_WINDOW_END_TIME_MUST_BE_N_HOURS_LATER_THAN_PREV_STEP,
} from '../validationSchema'
import { CAMPAIGN_STATUS_ENUM, publishCampaign, selectEditingCampaign, submitCampaign } from 'state/campaigns'
import { PLATFORM_CONTENT_TYPE_ENUM, STEP_TYPE_ENUM, TaskTypes } from 'services/api/constants'
import TaskSetupEditor from './TaskSetupEditor'
import { TaskSections } from '..'
import CustomSaveAndSubmitButtonGroup from './CustomSaveAndSubmitButtonGroup'
import { useDispatch, useSelector } from 'react-redux'
import { EditorValues } from './types'
import StepsEditor from './StepsEditor'
import { selectIsEngagementStepTypeHelper, selectTaskPlatformContentTypesByName } from 'state/constants'
import CustomScheduleEditor from './CustomScheduleEditor'
import CustomPayoutEditor from './CustomPayoutEditor'
import { ERROR_CODES, selectTaskErrors } from 'state/error'
import { ERROR_MESSAGES } from '../errorMessages'
import { runValidateSchema, setInitialTouched, validateFieldsAreAllPassed } from 'components/utils'
import { EDITOR_ROUTE_ENUM, ViewTypeContext } from 'components/Campaign'
import MaskWrapper from 'components/Campaign/Editor/MaskWrapper'
import CompareChangesPopup, { RecordChangesKey } from 'components/Shared/CompareChangesPopup'
import { CampaignStatusControl } from 'components/Shared/Permission/enums'
import { usePermission } from 'hooks/permissions'
import { SelectedEngagementConfig } from '../PredefinedTaskEditor/PayoutEditor'
import { selectIsExternalUser } from 'state/profile'
import { useStepEditorInitialValues } from 'hooks/steps'

interface CustomTaskEditorProps {
    taskSettings: TaskTypes
    task: TaskState
    saveTask: (task: TaskState, idle_hours_to_submit_engagement?: number, successCallback?: () => void) => void
    removeTask: (uuid: string) => void
    selectedTaskSection: TaskSections
    campaignUuid: string
}

const payoutsExtraFields = ['title', 'description', 'cost', 'min', 'max']
const blockFields = ['title', 'description', 'allow_download', 'files', 'max_photos']

export const CustomTaskEditor: React.FC<CustomTaskEditorProps> = ({
    task,
    saveTask,
    selectedTaskSection,
    campaignUuid,
}) => {
    const [t] = useNewTranslation()
    const isEngagementStepType = useSelector(selectIsEngagementStepTypeHelper)

    const onAddOrRemoveStepRef = useRef(false)
    const isPublish = useRef(true)
    const viewType = useContext(ViewTypeContext)
    const isExternalUser = useSelector(selectIsExternalUser)
    const dispatch = useDispatch()
    const taskPlatformContentTypesByName = useSelector(selectTaskPlatformContentTypesByName)

    const taskError = useSelector(selectTaskErrors(task.uuid))
    const campaign = useSelector(selectEditingCampaign)
    const registrationDate = useSelector(selectRegistrationDate)
    const initialValues = useStepEditorInitialValues(campaign, task)

    const campaignStatus = campaign ? campaign.status : undefined

    const canPublishCampaign =
        (campaign?.status === CAMPAIGN_STATUS_ENUM.FOR_REVIEW ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.WAIT_FOR_PAYMENT ||
            campaign?.status === CAMPAIGN_STATUS_ENUM.READY_FOR_LAUNCH) &&
        usePermission(CampaignStatusControl.LaunchCampaign)

    const isCampaignDraftOrForEdit =
        campaignStatus === CAMPAIGN_STATUS_ENUM.DRAFT || campaignStatus === CAMPAIGN_STATUS_ENUM.FOR_EDIT

    const validationSchema = useMemo(() => {
        if (!campaign) return
        const registrationStartDate = registrationDate.start
            ? parseBackendDate(registrationDate.start, campaign?.country)
            : null
        const registrationEndDate = registrationDate.end
            ? parseBackendDate(registrationDate.end, campaign?.country, {
                  showTime: false,
                  showYear: true,
                  addSeconds: -1,
              })
            : null
        const registrationTimeWindow = {
            start: registrationStartDate,
            end: registrationEndDate,
            isRegistrationTimeComparison: false,
        }
        const payoutsSchemaByPlatformContentType = Object.keys(taskPlatformContentTypesByName).reduce(
            (memo: Record<string, yup.ObjectSchema>, platformContentType: string) => {
                const engagementMetricsSchema =
                    isCampaignDraftOrForEdit ||
                    taskPlatformContentTypesByName[platformContentType].engagement_types.length === 0
                        ? yup.array().default([]).notRequired()
                        : yup
                              .array()
                              .default([])
                              .min(
                                  1,
                                  ERROR_MESSAGES['engagementMetrics'][
                                      ERROR_CODES.TASK_MUST_HAVE_AT_LEAST_ONE_ENGAGEMENT
                                  ],
                              )

                const engagementPayoutTypes = taskPlatformContentTypesByName[platformContentType].engagement_types.map(
                    type => type.id,
                )
                const engagementPayoutSchemas = engagementPayoutTypes.reduce(
                    (yupMemo: Record<string, yup.ObjectSchema>, payoutType: string) => {
                        yupMemo[payoutType] = yup.object().when('engagementMetrics', {
                            is: engagementMetrics =>
                                engagementMetrics
                                    ? engagementMetrics.some((metric: { value: string }) => metric.value === payoutType)
                                    : false,
                            then: yup.object().shape({
                                cost: PAYOUTS_SCHEMA(isCampaignDraftOrForEdit).cost,
                                min: PAYOUTS_SCHEMA(isCampaignDraftOrForEdit).min,
                                max: PAYOUTS_SCHEMA(isCampaignDraftOrForEdit).max,
                            }),
                        })
                        return yupMemo
                    },
                    {},
                )

                memo[platformContentType] = yup.object().shape({
                    engagementMetrics: engagementMetricsSchema,
                    ...engagementPayoutSchemas,
                })
                return memo
            },
            {},
        )

        const customStepSchema = yup.object().shape({
            data: yup.object().shape({
                title: STEP_DATA_SCHEMAS(isCampaignDraftOrForEdit).title,
                blocks: yup.array().of(CUSTOM_STEP_BLOCK_SCHEMA(isCampaignDraftOrForEdit)),
            }),
        })

        const engagementStepSchema = yup.object().shape({
            data: yup.object().shape({
                title: STEP_DATA_SCHEMAS(isCampaignDraftOrForEdit).title,
            }),
            extra_data: STEP_DATA_SCHEMAS(isCampaignDraftOrForEdit).extra_data,
        })

        return yup.object().shape({
            title: TASK_TITLE_SCHEMA(isCampaignDraftOrForEdit),
            platform_content_type: !isCampaignDraftOrForEdit
                ? yup.string().nullable().required(t('errorMessage.validation.selectTaskType'))
                : yup.string().nullable(),
            platform_content_type_others: yup
                .string()
                .nullable()
                .max(100, t('errorMessage.validation.characterLimitOneHundred'))
                .when('platform_content_type', {
                    is: value => value === PLATFORM_CONTENT_TYPE_ENUM.OTHERS && !isCampaignDraftOrForEdit,
                    then: yup
                        .string()
                        .required(_.get(ERROR_MESSAGES, `platform_content_type_others.${ERROR_CODES.INPUT_REQUIRED}`)),
                }),
            steps: yup.array().of(
                yup.lazy(
                    // reference for second parameter: https://github.com/jquense/yup/issues/204#issuecomment-537344424
                    // need to disable ts check here because yup.lazy doesnt accept function with second parameter, but we need it to get the item index
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    (item: StepState, stepItemOption: any): yup.Schema<any> => {
                        const rootValue = stepItemOption.from[0].value
                        const itemIndex = rootValue.steps.indexOf(item)
                        const previousStepIndex = itemIndex > 0 ? itemIndex - 1 : 0
                        if (item.type === STEP_TYPE_ENUM.CUSTOM) {
                            return customStepSchema.concat(
                                yup.object().shape({
                                    time_window: STEP_TIME_WINDOW_SCHEMA(
                                        campaign,
                                        initialValues,
                                        isExternalUser,
                                        previousStepIndex.toString(),
                                        registrationTimeWindow,
                                        isPublish,
                                    ),
                                }),
                            )
                        } else if (isEngagementStepType(item.type)) {
                            const platformContentType = rootValue.platform_content_type
                            const idle_hours_to_submit_engagement =
                                taskPlatformContentTypesByName[platformContentType].idle_hours_to_submit_engagement || 0

                            const schema = engagementStepSchema.concat(
                                yup.object().shape({
                                    time_window: STEP_TIME_WINDOW_SCHEMA(
                                        campaign,
                                        initialValues,
                                        isExternalUser,
                                        previousStepIndex.toString(),
                                        registrationTimeWindow,
                                        isPublish,
                                    ),
                                }),
                            )

                            // if has idle_hours_to_submit_engagement and has previousStep
                            if (itemIndex > 0 && idle_hours_to_submit_engagement > 0) {
                                return schema.concat(
                                    yup.object().shape({
                                        time_window: SCHEMA_TIME_WINDOW_END_TIME_MUST_BE_N_HOURS_LATER_THAN_PREV_STEP(
                                            null,
                                            idle_hours_to_submit_engagement,
                                        ),
                                    }),
                                )
                            } else {
                                return schema
                            }
                        }
                        return yup.mixed().notRequired()
                    },
                ),
            ),
            payouts: yup
                .object()
                .shape({
                    base: yup.object().shape({
                        cost: PAYOUTS_SCHEMA(isCampaignDraftOrForEdit).cost,
                    }),
                    extras: EXTRA_PAYOUT_SCHEMA(isCampaignDraftOrForEdit),
                })
                .when(
                    ['platform_content_type', 'steps'],
                    (platformContentType: string, steps: EditorValues['steps']) => {
                        if (!platformContentType) {
                            return yup.mixed()
                        }
                        if (steps.some(step => isEngagementStepType(step.type))) {
                            return payoutsSchemaByPlatformContentType[platformContentType]
                        }
                        return yup.mixed()
                    },
                ),
        })
    }, [isCampaignDraftOrForEdit, taskPlatformContentTypesByName, isEngagementStepType, campaign])

    const initialErrors = useMemo(() => {
        if (!taskError || !initialValues?.steps) {
            return {}
        }

        const returnError: Record<string, any> = {}
        if (taskError['title']) {
            returnError['title'] = _.get(ERROR_MESSAGES, `title.${taskError['title']}`, taskError['title'])
        }
        if (taskError['platform_content_type']) {
            returnError['platform_content_type'] = _.get(
                ERROR_MESSAGES,
                `platform_content_type.${taskError['platform_content_type']}`,
                taskError['platform_content_type'],
            )
        }
        if (taskError['platform_content_type_others']) {
            returnError['platform_content_type_others'] = _.get(
                ERROR_MESSAGES,
                `platform_content_type_others.${taskError['platform_content_type_others']}`,
                taskError['platform_content_type_others'],
            )
        }

        if (taskError['steps']) {
            if (typeof taskError['steps'] === 'string') {
                const errorMessage = _.get(ERROR_MESSAGES, `steps.${taskError['steps']}`, taskError['steps'])
                _.set(returnError, `steps`, errorMessage)
            } else {
                for (const stepUuid in taskError['steps']) {
                    const stepIndex = initialValues.steps.findIndex(step => step.uuid === stepUuid)

                    const engagementTypesError = _.get(taskError, `steps.${stepUuid}.engagement_types`)
                    if (engagementTypesError) {
                        const errorMessage = _.get(
                            ERROR_MESSAGES,
                            `engagementMetrics.${engagementTypesError}`,
                            engagementTypesError,
                        )
                        _.set(returnError, 'payouts.engagementMetrics', errorMessage)
                    }

                    const stepTitleError = _.get(taskError, `steps.${stepUuid}.data.title`)
                    if (stepTitleError) {
                        const errorMessage = _.get(ERROR_MESSAGES, `steps.title.${stepTitleError}`, stepTitleError)
                        _.set(returnError, `steps.${stepIndex}.data.title`, errorMessage)
                    }

                    const blocksError = _.get(taskError, `steps.${stepUuid}.data.blocks`)
                    if (blocksError) {
                        Object.keys(blocksError).forEach(blockIndex => {
                            Object.keys(blocksError[blockIndex]).forEach(key => {
                                const errorMessage = _.get(
                                    ERROR_MESSAGES,
                                    `steps.blocks.${key}.${blocksError[blockIndex][key]}`,
                                    blocksError[blockIndex][key],
                                )
                                _.set(returnError, `steps.${stepIndex}.data.blocks.${blockIndex}.${key}`, errorMessage)
                            })
                        })
                    }

                    const timeWindowError = _.get(taskError, `steps.${stepUuid}.time_window`)
                    if (timeWindowError) {
                        Object.keys(timeWindowError).forEach(key => {
                            const errorMessage = _.get(
                                ERROR_MESSAGES,
                                `time_window.${key}.${timeWindowError[key]}`,
                                timeWindowError[key],
                            )
                            _.set(returnError, `steps.${stepIndex}.time_window.${key}`, errorMessage)
                        })
                    }

                    const extraDataErrorCode = _.get(taskError, `steps.${stepUuid}.data.extra_data`)
                    if (extraDataErrorCode) {
                        const errorMessage = _.get(
                            ERROR_MESSAGES,
                            `steps.extra_data.${extraDataErrorCode}`,
                            extraDataErrorCode,
                        )
                        _.set(returnError, `steps.${stepIndex}.data.extra_data`, errorMessage)
                    }
                }
            }
        }

        if (taskError['payouts']) {
            if (taskError['payouts']['base']) {
                if (_.isEmpty(_.get(initialValues, 'payouts.base.cost'))) {
                    const errorMessage = _.get(ERROR_MESSAGES, `payouts.base.${taskError['payouts']['base']}`)
                    _.set(returnError, 'payouts.base.cost', errorMessage)
                }
            }
            const payoutExtrasError = _.get(taskError, 'payouts.extras')
            if (payoutExtrasError) {
                for (const uuid in payoutExtrasError) {
                    payoutsExtraFields.forEach(field => {
                        if (_.has(payoutExtrasError, `${uuid}.${field}`)) {
                            const errorMessage = _.get(
                                ERROR_MESSAGES,
                                `payouts.${field}.${payoutExtrasError[uuid][field]}`,
                            )
                            _.set(returnError, `payouts.extras.${uuid}.${field}`, errorMessage)
                        }
                    })
                }
            }
        }
        return returnError
    }, [taskError, initialValues])

    const initialTouched = useMemo(() => setInitialTouched(initialErrors), [initialErrors])

    const validate = useCallback(
        async function (values: EditorValues) {
            const schemaErrors = await runValidateSchema(values, validationSchema)
            if (_.isEmpty(initialErrors) || !isPublish.current) {
                return schemaErrors
            }

            const returnError = _.cloneDeep(initialErrors)
            if (initialErrors['title'] && values.title) {
                returnError['title'] = _.get(schemaErrors, 'title', '')
            }
            if (initialErrors['platform_content_type'] && values.platform_content_type) {
                returnError['platform_content_type'] = _.get(schemaErrors, 'platform_content_type', '')
            }
            if (initialErrors['platform_content_type_others'] && values.platform_content_type_others) {
                returnError['platform_content_type_others'] = _.get(schemaErrors, 'platform_content_type_others', '')
            }
            if (typeof initialErrors['steps'] === 'string' && !_.isEmpty(values.steps)) {
                returnError['steps'] = ''
            }
            if (initialErrors['steps'] && Array.isArray(initialErrors['steps']) && !_.isEmpty(values.steps)) {
                initialErrors['steps'].forEach((stepError, stepIndex) => {
                    if (!_.has(values, `steps.${stepIndex}`)) {
                        _.set(returnError, `steps.${stepIndex}`, undefined)
                        return
                    }

                    if (_.get(stepError, 'data.title') && _.get(values, `steps.${stepIndex}.data.title`)) {
                        _.set(
                            returnError,
                            `steps.${stepIndex}.data.title`,
                            _.get(schemaErrors, `steps.${stepIndex}.data.title`, ''),
                        )
                    }

                    if (_.get(values, `steps.${stepIndex}.type`) === STEP_TYPE_ENUM.CUSTOM) {
                        if (_.get(stepError, `data.blocks`)) {
                            _.get(stepError, `data.blocks`).forEach((blockError: unknown, blockIndex: number) => {
                                if (!_.has(values, `steps.${stepIndex}.data.blocks.${blockIndex}`)) {
                                    _.set(returnError, `steps.${stepIndex}.data.blocks.${blockIndex}`, undefined)
                                    return
                                }
                                blockFields.forEach(blockField => {
                                    if (
                                        _.get(blockError, blockField) &&
                                        _.get(values, `steps.${stepIndex}.data.blocks.${blockIndex}.${blockField}`)
                                    ) {
                                        _.set(
                                            returnError,
                                            `steps.${stepIndex}.data.blocks.${blockIndex}.${blockField}`,
                                            _.get(
                                                schemaErrors,
                                                `steps.${stepIndex}.data.blocks.${blockIndex}.${blockField}`,
                                                '',
                                            ),
                                        )
                                    }
                                })
                            })
                        }
                    }

                    const stepExtraData = _.get(values, `steps.${stepIndex}.data.extra_data`)
                    if (Array.isArray(stepExtraData)) {
                        if (
                            stepExtraData.length === 0 ||
                            stepExtraData.every(extraData => extraData.title && extraData.description)
                        ) {
                            _.set(returnError, `steps.${stepIndex}.data.extra_data`, '')
                        }
                    }

                    if (
                        _.get(stepError, 'time_window.start') &&
                        _.get(values, `steps.${stepIndex}.time_window.start`)
                    ) {
                        _.set(
                            returnError,
                            `steps.${stepIndex}.time_window.start`,
                            _.get(schemaErrors, `steps.${stepIndex}.time_window.start`, ''),
                        )
                    }

                    if (_.get(stepError, 'time_window.end') && _.get(values, `steps.${stepIndex}.time_window.end`)) {
                        _.set(
                            returnError,
                            `steps.${stepIndex}.time_window.end`,
                            _.get(schemaErrors, `steps.${stepIndex}.time_window.end`, ''),
                        )
                    }
                })
            }

            if (_.get(initialErrors, 'payouts.base.cost') && _.get(values, 'payouts.base.cost')) {
                _.set(returnError, 'payouts.base.cost', _.get(schemaErrors, 'payouts.base.cost', ''))
            }
            if (_.get(initialErrors, 'payouts.extras') && _.get(values, 'payouts.extras')) {
                Object.keys(initialErrors['payouts']['extras']).forEach(uuid => {
                    _.set(returnError, `payouts.extras.${uuid}`, {})
                    payoutsExtraFields.forEach(field => {
                        if (
                            _.get(initialErrors, `payouts.extras.${uuid}.${field}`) &&
                            _.get(values, `payouts.extras.${uuid}.${field}`)
                        ) {
                            _.set(
                                returnError,
                                `payouts.extras.${uuid}.${field}`,
                                _.get(schemaErrors, `payouts.extras.${uuid}.${field}`, ''),
                            )
                        }
                    })
                })
            }

            const payoutEngagementMetricsValue = _.get(values, `payouts.engagementMetrics`)
            if (_.get(initialErrors, `payouts.engagementMetrics`) && !_.isEmpty(payoutEngagementMetricsValue)) {
                _.set(returnError, `payouts.engagementMetrics`, _.get(schemaErrors, `payouts.engagementMetrics`, ''))
            }
            return validateFieldsAreAllPassed(returnError) ? {} : returnError
        },
        [validationSchema, initialErrors],
    )

    const handleSubmit = useCallback(
        (values: EditorValues, actions) => {
            if (!campaign) return
            const { payouts } = values

            const parsedStepData = values.steps.map(step => {
                const startTime = step?.time_window?.start
                const endTime = step?.time_window?.end
                return {
                    ...step,
                    time_window: {
                        start: startTime ? convertToBackendStr(startTime, campaign.country) : null,
                        end: endTime ? convertToBackendStr(addDay(endTime, 1), campaign.country) : null,
                    },
                }
            })

            const selectedEngagementValues =
                payouts && payouts.engagementMetrics
                    ? payouts.engagementMetrics.map(
                          (engagementMetric: SelectedEngagementConfig) => engagementMetric.value,
                      )
                    : []

            const engagementPayoutTypes = values.platform_content_type
                ? taskPlatformContentTypesByName[values.platform_content_type].engagement_types.map(item => item.id)
                : []

            const requestBody = Object.assign({}, values, {
                steps: parsedStepData,
                payouts: Object.keys(payouts).reduce((memo: any, payoutKey: string) => {
                    const isValidNoneExtraPayoutKey =
                        payoutKey !== 'extras' &&
                        payoutKey !== 'engagementMetrics' && // used for engagement checkbox, no need to includes in payout
                        (!engagementPayoutTypes.includes(payoutKey as EngagementId) ||
                            selectedEngagementValues.includes(payoutKey))

                    if (payoutKey !== 'extras') {
                        if (isValidNoneExtraPayoutKey) {
                            memo[payoutKey] = payouts[payoutKey]
                        }
                    } else {
                        memo.extras = payouts.extras
                    }
                    return memo
                }, {}),
            })

            // remove null value from requestBody except 'platform_content_type' and 'platform_content_type_others'
            const preparedRequestBody = _.omitBy(requestBody, (value, key) => {
                if (key === 'platform_content_type' || key === 'platform_content_type_others') {
                    return false
                }
                return _.isNil(value)
            }) as TaskState

            if (isPublish.current) {
                saveTask(preparedRequestBody, undefined, () => {
                    if (canPublishCampaign) {
                        dispatch(
                            publishCampaign({
                                data: { campaign: {} as any, uuid: campaignUuid },
                            }),
                        )
                    } else {
                        dispatch(
                            submitCampaign({
                                data: { campaign: {} as any, uuid: campaignUuid },
                            }),
                        )
                    }
                })
            } else if (onAddOrRemoveStepRef.current) {
                saveTask(preparedRequestBody, undefined, noop)
            } else {
                saveTask(preparedRequestBody)
            }

            actions.setSubmitting(false)
        },
        [saveTask, campaignUuid, campaignStatus, canPublishCampaign],
    )

    if (!campaign || !initialValues) return null

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues as EditorValues}
            initialErrors={initialErrors}
            initialTouched={initialTouched}
            onSubmit={handleSubmit}
            validate={validate}
        >
            {formikBag => {
                return (
                    <CompareChangesPopup
                        initialData={formikBag.initialValues}
                        compareData={formikBag.values}
                        recordChangesKey={RecordChangesKey.IS_CAMPAIGN_VALUE_CHANGE}
                    >
                        <Form>
                            <OnFormikValidationError
                                callback={() => {
                                    console.error('Formik Validation Errors: ', formikBag.errors)
                                    showSnackBar({
                                        title: t('errorMessage.general.cantSaveChanges'),
                                        type: 'error',
                                    })
                                }}
                            />
                            <MaskWrapper>
                                {(function (): React.ReactNode {
                                    switch (selectedTaskSection) {
                                        case TaskSections.SETUP:
                                            return (
                                                <div className="form-field">
                                                    <TaskSetupEditor
                                                        onConfirmDeletingEngagementMetric={() => {
                                                            isPublish.current = false
                                                            formikBag.submitForm()
                                                        }}
                                                        taskUuid={task.uuid}
                                                    />
                                                </div>
                                            )
                                        case TaskSections.STEPS:
                                            return (
                                                <div
                                                    className={`form-field ${
                                                        formikBag.values.steps.length > 1 ? 'no-background' : ''
                                                    }`}
                                                >
                                                    <StepsEditor
                                                        onStepsChanged={() => {
                                                            isPublish.current = false
                                                            onAddOrRemoveStepRef.current = true
                                                            formikBag.submitForm().then(() => {
                                                                onAddOrRemoveStepRef.current = false
                                                            })
                                                        }}
                                                        campaignStatus={campaignStatus}
                                                    />
                                                </div>
                                            )
                                        case TaskSections.SCHEDULE:
                                            return (
                                                <div className="form-field">
                                                    <CustomScheduleEditor taskUuid={task.uuid} />
                                                </div>
                                            )
                                        case TaskSections.PAYOUT:
                                            return (
                                                <div className="form-field">
                                                    <CustomPayoutEditor
                                                        campaignUuid={campaignUuid}
                                                        taskUuid={task.uuid}
                                                    />
                                                </div>
                                            )
                                        default:
                                            return undefined
                                    }
                                })()}
                            </MaskWrapper>
                            {viewType === EDITOR_ROUTE_ENUM.EDIT && (
                                <CustomSaveAndSubmitButtonGroup
                                    selectedTaskSection={selectedTaskSection}
                                    campaign={campaign}
                                    disableSave={false}
                                    disableSubmit={!formikBag.isValid}
                                    onSubmit={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                        e.preventDefault()
                                        isPublish.current = true
                                        formikBag.submitForm()
                                    }}
                                    onSave={(e: React.MouseEvent<HTMLButtonElement>): void => {
                                        e.preventDefault()
                                        isPublish.current = false
                                        formikBag.submitForm()
                                    }}
                                    onStepAdded={() => {
                                        isPublish.current = false
                                        onAddOrRemoveStepRef.current = true
                                        formikBag.submitForm().then(() => {
                                            onAddOrRemoveStepRef.current = false
                                        })
                                    }}
                                />
                            )}
                        </Form>
                    </CompareChangesPopup>
                )
            }}
        </Formik>
    )
}

export default CustomTaskEditor
